import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Upload, Search, Download, Eye, Trash2, ExternalLink } from 'lucide-react';
// import { useFirebase } from '../../hooks/use-firebase';
import FileUploadDialog from './FileUploadDialog';
import FileIcon from './FileIcon';
import { FileItem } from '../../types/file';
import { app } from "../../firebase";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";

interface CategoryFileManagerProps {
  organization: string;
  category: string;
}

interface CategoryFile extends FileItem {
  organization: string;
  category: string;
  url: string;
  downloadUrl?: string;
  createdBy: string;
  tags: string[];
  type?: any;
}

// Helper to get Firestore collection name based on org/category
const getCollectionName = (organization: string, category: string) => {
  // Normalize category: remove dashes/underscores, lowercase first letter
  const norm = (str) => str.replace(/[-_\s]/g, '').replace(/^([A-Z])/, m => m.toLowerCase());
  const cat = norm(category);
  if (organization === "bytesplash") {
    switch (cat) {
      case "offerletters": return "bytesplash_offerletters";
      case "coursecertificates": return "bytesplash_courseCertificates";
      case "idcards": return "bytesplash_iDCards";
       case "drivequestionpapers": return "bytesplash_drivequestionpapers";
      case "logos": return "bytesplash_Logos";
      case "admin": return "bytesplash_admin";
      case "idcardthreads": return "bytesplash_IDCardThreads";
      case "employmentcertificates": return "bytesplash_employmentCertificates";
      case "materials": return "bytesplash_materials";
      case "recordedvideos": return "bytesplash_recordedVideos";
      case "jobbonds": return "bytesplash_jobBonds";
      case "payslips": return "bytesplash_payslips";
      case "placementposters": return "bytesplash_placementPosters";
      case "allforms": return "bytesplash_allforms";
      case "pomplets": return "bytesplash_pomplets";
      case 'syllabusandtemplates': return 'bytesplash_syllabusandtemplates';
      case 'hrinvites': return 'bytesplash_hrinvites';
      case 'crtinvites': return 'bytesplash_crtinvites';
      default: return `bytesplash_${cat}`;
    }
  } else if (organization === "margeylabs") {
    switch (cat) {
      case "offerletters": return "margeylabs_offerletters";
      case "internshipcertificates": return "margeylabs_InternshipCertificates";
      case "idcards": return "margeylabs_IDCards";
      case "logos": return "margeylabs_Logos";
      case "idcardthreads": return "margeylabs_IDCardThreads";
      case "materials": return "margeylabs_materials";
      case "recordedvideos": return "margeylabs_recordedVideos";
      case "hrresources": return "margeylabs_hrResources";
      default: return `margeylabs_${cat}`;
    }
  }
  return `${organization}_${cat}`;
};

// Query files from Firestore
const queryDocs = async (organization: string, category: string) => {
  const db = getFirestore(app);
  const collectionName = getCollectionName(organization, category);
  const colRef = collection(db, collectionName);
  const snapshot = await getDocs(colRef);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Upload file to Storage and metadata to Firestore
const create = async (organization: string, category: string, file: File, fileData: any) => {
  const db = getFirestore(app);
  const storage = getStorage(app);
  const collectionName = getCollectionName(organization, category);
  // Upload file to storage
  const storageRef = ref(storage, `${collectionName}/${file.name}`);
  await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(storageRef);
  // Store metadata in Firestore
  const docRef = doc(collection(db, collectionName));
  await setDoc(docRef, { ...fileData, url: downloadUrl, downloadUrl });
  return { id: docRef.id, ...fileData, url: downloadUrl, downloadUrl };
};

// Remove file from Storage and Firestore
const remove = async (organization: string, category: string, fileId: string, fileName: string) => {
  const db = getFirestore(app);
  const storage = getStorage(app);
  const collectionName = getCollectionName(organization, category);
  // Delete Firestore doc
  await deleteDoc(doc(db, collectionName, fileId));
  // Delete from storage
  const storageRef = ref(storage, `${collectionName}/${fileName}`);
  await deleteObject(storageRef);
};

const CategoryFileManager: React.FC<CategoryFileManagerProps> = ({ organization, category }) => {
  const [files, setFiles] = useState<CategoryFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<CategoryFile | null>(null);
  const { toast } = useToast();

  const db = getFirestore(app);
  const auth = getAuth(app);
  const [fetchAll, setFetchAll] = useState<any>('organization_files');


  useEffect(() => {
    loadFiles();
    // eslint-disable-next-line
  }, [organization, category]);

  const loadFiles = async () => {
    setLoading(true);
    try {
      const allFiles = await queryDocs(organization, category);
      setFiles(allFiles as CategoryFile[]);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load files",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };


  const handleFileUpload = async (uploadedFiles: File[]) => {
    for (const file of uploadedFiles) {
      try {
        // Get user email from localStorage (assume key is 'loggedINUser' and value is JSON with 'email')
        let userEmail = 'unknown-user';
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const userObj = JSON.parse(userStr);
            if (userObj.emailId) userEmail = userObj.emailId;
          }
        } catch {}
        const fileData: Omit<CategoryFile, 'id'> = {
          name: file.name,
          type: getFileType(file),
          size: formatFileSize(file.size),
          uploadDate: new Date().toISOString().split('T')[0],
          organization,
          category,
          createdBy: userEmail,
          tags: [],
          url: null
        };
        await create(organization, category, file, fileData);
        toast({
          title: "Success",
          description: `${file.name} uploaded successfully`
        });
      } catch (error) {
        toast({
          title: "Error",
          description: `Failed to upload ${file.name}`,
          variant: "destructive"
        });
      }
    }
    loadFiles();
    // Refresh org menu counts if available
    if (typeof window !== 'undefined' && (window as any).refetchOrgMenuCounts) {
      (window as any).refetchOrgMenuCounts();
    }
    setUploadDialogOpen(false);
  };


  const handleDelete = async () => {
    if (!fileToDelete) return;
    try {
      await remove(organization, category, fileToDelete.id, fileToDelete.name);
      toast({
        title: "Success",
        description: "File deleted successfully"
      });
      loadFiles();
      // Refresh org menu counts if available
      if (typeof window !== 'undefined' && (window as any).refetchOrgMenuCounts) {
        (window as any).refetchOrgMenuCounts();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete file",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const handleDownload = (file: CategoryFile) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileType = (file: { name: string; type?: string }) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (ext === 'pdf') return 'pdf';
    if (ext === 'doc' || ext === 'docx') return 'word';
    if (ext === 'ppt' || ext === 'pptx') return 'powerpoint';
    if (["jpg","jpeg","png","gif","bmp","svg","webp"].includes(ext)) return 'image';
    if (["mp4","mov","avi","wmv","flv","mkv","webm"].includes(ext)) return 'video';
    if (["mp3","wav","aac","ogg","flac"].includes(ext)) return 'audio';
    if (ext === 'txt') return 'document';
    return 'other';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCategoryDisplayName = () => {
    return category.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getOrganizationDisplayName = () => {
    return organization === 'bytesplash' ? 'ByteSplash' : 'Margey Labs';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <div className="text-muted-foreground">Loading files...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold">{getCategoryDisplayName()}</h2>
          <p className="text-sm sm:text-base text-muted-foreground">{getOrganizationDisplayName()} • {files.length} files</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)} className="gap-2 w-full sm:w-auto">
          <Upload className="h-4 w-4" />
          Upload Files
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle className="text-lg sm:text-xl">File Management</CardTitle>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredFiles.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? 'No files match your search' : 'No files uploaded yet'}
            </div>
          ) : (
            <div className="grid gap-3 sm:gap-4">
              {filteredFiles.map((file) => (
                <div key={file.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-muted/50 transition-colors gap-3">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <FileIcon type={getFileType(file)} className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm sm:text-base truncate">{file.name}</h4>
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground">
                        <span>{file.size}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{file.uploadDate}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>by {file.createdBy}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                    <Badge variant="outline" className="capitalize text-xs">
                      {getFileType(file)}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDownload(file)}
                      className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-primary/10 transition-colors"
                      aria-label="Download"
                    >
                      <Download className="h-5 w-5 text-primary" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setFileToDelete(file); setDeleteDialogOpen(true); }}
                      className="h-9 w-9 flex items-center justify-center rounded-full hover:bg-destructive/10 transition-colors"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-5 w-5 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <FileUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onFileUpload={handleFileUpload}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete File</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            Are you sure you want to delete <span className="font-semibold">{fileToDelete?.name}</span>? This action cannot be undone.
          </div>
          <DialogFooter className="flex flex-row gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => { setDeleteDialogOpen(false); setFileToDelete(null); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoryFileManager;